import {Component, Inject, OnDestroy} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {Subject, take, takeUntil} from 'rxjs';
import {ReferenceLookupValue} from 'src/app/modules/clm/types/reference-lookup-value.enum';
import {CustomValidator} from '../../../../common';
import {DisplayStrings} from '../../../../common/constants/DisplayStrings';
import {ApplicationBankDetailsDTO} from '../../../models/application/ApplicationBankDetailsDTO';
import {ApplicationPersonDTO} from '../../../models/application/ApplicationPersonDTO';
import {ReferenceDTO} from '../../../models/application/ReferenceDTO';
import {ReferenceService} from '../../../services/reference.service';

enum Controls {
  Usage = 'usage',
  BankCode = 'bankCode',
  BankAccountType = 'bankAccountType',
  BranchCode = 'branchCode',
  AccountNumber = 'accountNumber',
  AccountName = 'accountName',
  DebitOrderDay = 'debitOrderDay',
  IsThirdPartyAccount = 'isThirdPartyAccount',
  IsVerified = 'isVerified',
}

@Component({
  selector: 'app-application-bank-detail-update-dialog',
  templateUrl: './application-bank-detail-update-dialog.html',
  styleUrls: [
    '../../../../../../styles/global/_dialog-styles.scss',
  ]
})
export class ApplicationBankDetailDialogComponent implements OnDestroy{
  readonly destroyed$ = new Subject<boolean>();
  readonly controls = Controls;
  readonly BACK_ACCOUNT_TYPE = {
    CHEQUE_CURRENT_ACCOUNT: 'CA',
  };
  readonly REFUND_ONLY_USAGE_CODE = 'Z';
  protected readonly DisplayStrings = DisplayStrings;
  protected readonly CustomValidator = CustomValidator;

  heading = 'Add Bank Details';
  bankDetailFormGroup: FormGroup;
  usages: ReferenceDTO[] = [];
  bankCodes: ReferenceDTO[] = [];
  bankAccountTypes: ReferenceDTO[] = [];
  debitOrderDays: ReferenceDTO[] = [];
  showDebitOrderDate = false;
  principleMember: ApplicationPersonDTO;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: {
      bankAccountDetails: ApplicationBankDetailsDTO,
      hasContributionsAccount?: boolean,
      hasRefundsAccount?: boolean,
      USAGE?: {
        readonly CONTRIBUTIONS: string;
        readonly REFUNDS: string;
        readonly BOTH: string;
      },
      principleMember?: ApplicationPersonDTO,
    },
    private formBuilder: FormBuilder,
    public dialogRef: MatDialogRef<ApplicationBankDetailDialogComponent>,
    private referenceService: ReferenceService
  ) {
    this.principleMember = data?.principleMember;
    this.initForm();
    this.initReferenceValues();
    if (data?.bankAccountDetails) {
      this.setFormValues(data.bankAccountDetails);
      this.heading = 'Edit Bank Details';
      this.initSubscriptions();
      this.updateDebitOrderDateBasedOnUsage(data.bankAccountDetails?.usage);
    }
    else {
      this.initSubscriptions();
      const isThirdParty = data?.bankAccountDetails?.isThirdPartyAccount;
      this.bankDetailFormGroup.patchValue(
        {
          [Controls.AccountName]: data.principleMember && !isThirdParty ? `${data.principleMember?.title} ${data.principleMember?.firstName} ${data.principleMember?.lastName}` : '',
          [Controls.BankAccountType]: this.BACK_ACCOUNT_TYPE.CHEQUE_CURRENT_ACCOUNT,
        });
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
  initReferenceValues(): void {
    this.getListOfLookupValues(ReferenceLookupValue.BankAccountType);
    this.getListOfLookupValues(ReferenceLookupValue.BankCode);
    this.getListOfLookupValues(ReferenceLookupValue.Usage);
    this.getListOfLookupValues(ReferenceLookupValue.DebitOrderDay);
  }

  initSubscriptions(): void {
    this.bankDetailFormGroup.get(Controls.Usage)?.valueChanges
        .pipe(takeUntil(this.destroyed$)).subscribe(usage => {
          this.updateDebitOrderDateBasedOnUsage(usage);
        });

    this.bankDetailFormGroup.get(Controls.IsThirdPartyAccount)?.valueChanges
    .pipe(takeUntil(this.destroyed$)).subscribe(isThirdPartyAccount => {
      this.updateNameBasedOnIsThirdParty(isThirdPartyAccount);
    });
  }

  updateDebitOrderDateBasedOnUsage(usage: string): void {
    const debitOrderControl = this.bankDetailFormGroup.get(Controls.DebitOrderDay);
    if (usage === this.REFUND_ONLY_USAGE_CODE) {
      this.showDebitOrderDate = false;
      debitOrderControl.setValue(null);
      debitOrderControl.clearValidators();
    } else {
      this.showDebitOrderDate = true;
      debitOrderControl.setValidators(Validators.required);
      if (debitOrderControl.value == null) {
        debitOrderControl.setValue(1);
      }
    }
    debitOrderControl.updateValueAndValidity();
  }

  updateNameBasedOnIsThirdParty(isThirdPartyAccount: boolean): void{

    const accountNameControl = this.bankDetailFormGroup.get(Controls.AccountName);
    let accountName = this.principleMember ? `${this.principleMember?.title} ${this.principleMember?.firstName} ${this.principleMember?.lastName}` : '';
    accountName = accountNameControl.value != '' ? accountNameControl.value : accountName;
    accountNameControl.setValue(!isThirdPartyAccount ? accountName : '');
    accountNameControl.updateValueAndValidity();
  }


  getListOfLookupValues(entity: string): void {
    this.referenceService.getAllByFieldName(entity).pipe(take(1)).subscribe(
      (response: ReferenceDTO[]) => {
        if (response) {
          if (entity === ReferenceLookupValue.BankCode) {
            this.bankCodes = this.sortReferenceArrayByValue(response);
          }
          if (entity === ReferenceLookupValue.BankAccountType) {
            this.bankAccountTypes = this.sortReferenceArrayByValue(response);
          }
          if (entity === ReferenceLookupValue.Usage) {
            this.usages = this.sortReferenceArrayByValue(response);
          }
          if (entity === ReferenceLookupValue.DebitOrderDay) {
            this.debitOrderDays = this.sortReferenceArrayByValue(response);
          }
        }
      },
    );
  }

  isUsageOptionDisabled(usageKey: string): boolean {
    // Will ensure that existing pages using this component is not influenced
    if (!this.data.USAGE) {
      return false;
    }

    const isEditMode = !!this.data.bankAccountDetails;

    const {REFUNDS, CONTRIBUTIONS, BOTH} = this.data.USAGE;
    const {hasRefundsAccount, hasContributionsAccount} = this.data;

    switch (usageKey) {
    case REFUNDS:
      return hasRefundsAccount;
    case CONTRIBUTIONS:
      return hasContributionsAccount;
    case BOTH:
      if (isEditMode) {
        return hasRefundsAccount && hasContributionsAccount;
      }
      else {
        return hasRefundsAccount || hasContributionsAccount;
      }
    default:
      return false;
    }
  }

  onSave(): void {
    if (this.bankDetailFormGroup.valid) {
      const formValue = this.bankDetailFormGroup.value;
      const isVerifiedValue = formValue[Controls.IsVerified];
      const isVerified = isVerifiedValue ? 1 : 0;

      const updatedBankDetails: ApplicationBankDetailsDTO = {
        ...this.data.bankAccountDetails,
        ...formValue,
        debitOrderDay: this.showDebitOrderDate ? this.bankDetailFormGroup.get(Controls.DebitOrderDay)?.value ?? null : null,
        isVerified,
      };

      this.dialogRef.close(updatedBankDetails);
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onBankSelectionChanged(bankCode: string): void {
    this.referenceService.getBy(bankCode, 'bankBranchCode')
      .pipe(take(1))
      .subscribe((reference: ReferenceDTO) => {
        if (reference) {
          this.bankDetailFormGroup.patchValue({
            [Controls.BranchCode]: reference.value
          });
        } else {
          this.bankDetailFormGroup.patchValue({
            [Controls.BranchCode]: ''
          });
        }
      });
  }

  private initForm(): void {
    this.bankDetailFormGroup = this.formBuilder.group({
      [Controls.Usage]: ['', Validators.required],
      [Controls.BankCode]: ['', Validators.required],
      [Controls.BankAccountType]: ['', Validators.required],
      [Controls.BranchCode]: ['', Validators.required],
      [Controls.AccountNumber]: ['', Validators.required],
      [Controls.AccountName]: ['', Validators.required],
      [Controls.DebitOrderDay]: ['1', Validators.required],
      [Controls.IsThirdPartyAccount]: [false],
      [Controls.IsVerified]: [false]
    });
  }

  private setFormValues(bankDetails: ApplicationBankDetailsDTO): void {
    this.bankDetailFormGroup.patchValue({
      [Controls.Usage]: bankDetails.usage,
      [Controls.BankCode]: bankDetails.bankCode,
      [Controls.BankAccountType]: bankDetails.bankAccountType,
      [Controls.BranchCode]: bankDetails.branchCode,
      [Controls.AccountNumber]: bankDetails.accountNumber,
      [Controls.AccountName]: bankDetails.accountName,
      [Controls.DebitOrderDay]: bankDetails.debitOrderDay?.toString(),
      [Controls.IsThirdPartyAccount]: bankDetails.isThirdPartyAccount,
      [Controls.IsVerified]: bankDetails.isVerified == 1
    });
  }

  private sortReferenceArrayByValue(array: ReferenceDTO[]): ReferenceDTO[] {
    if (!array || array.length === 0) {
      return [];
    }
    return array.sort((a, b) => {
      // Convert strings to numbers if they are numeric
      const aNum = Number(a.value);
      const bNum = Number(b.value);

      if (!isNaN(aNum) && !isNaN(bNum)) {
        return aNum - bNum;
      }

      return a.value.localeCompare(b.value);
    });
  }
}
